import { apiClient } from '@/lib/api/client'

export interface ProjectTypeFunnelDto {
  typeCode: string
  typeLabel: string
  projectsWithQuoteSent: number
  projectsWon: number
  projectsLost: number
  projectsPending: number
  conversionRateGlobal: number | null // Won / Sent %
  revenueWon: number                  // sum of first accepted quote amounts
  revenueShare: number | null         // revenueWon / total revenueWon %
  avgDealSize: number | null          // revenueWon / won (null when won = 0)
  avgDaysToClose: number | null       // first quote sent → first accepted (Won only)
}

/** DASH-004 — one aging bucket. bucket: "fresh" | "warming" | "critical" */
export interface PendingQuoteAgingBucketDto {
  bucket: 'fresh' | 'warming' | 'critical'
  count: number
  amount: number
}

export interface DashboardComparisonDto {
  label: string
  projectsCreated: number
  projectsWithQuoteSent: number
  projectsWon: number
  projectsLost: number
  conversionRateGlobal: number | null
  avgDealSize: number | null
}

export interface BusinessDashboardDto {
  period: string
  periodStart: string
  periodEnd: string

  revenueRealized: number
  invoiceCount: number

  // Live snapshot — NOT period-filtered
  revenueInProgress: number
  orderCount: number

  // Cohort funnel (period filter = project.created_at)
  projectsCreated: number
  projectsWithQuoteSent: number
  projectsWon: number
  projectsLost: number
  projectsPending: number

  // Conversion rates (null when denominator = 0)
  conversionRateGlobal: number | null   // Won / Sent
  conversionRateResolved: number | null // Won / (Won + Lost)

  // Average stage durations in days (null when no resolved projects)
  avgDaysCreatedToQuoteSent: number | null
  avgDaysQuoteSentToResolved: number | null

  // Panier moyen — cohort-based (null when no Won projects)
  avgDealSize: number | null

  // Live snapshot — NOT period-filtered
  pendingQuotesAmount: number
  pendingQuotesCount: number

  // Aging breakdown — DASH-004 (live snapshot, auto-lost excluded)
  pendingQuotesByAge: PendingQuoteAgingBucketDto[]

  // Performance by project type
  funnelByType: ProjectTypeFunnelDto[]

  // Period-over-period comparison — DASH-005 (null when no data)
  comparison: DashboardComparisonDto | null
}

export type DashboardPeriod = 'month' | 'quarter' | 'year' | '6months'

export function getBusinessDashboard(period: DashboardPeriod = 'month'): Promise<BusinessDashboardDto> {
  return apiClient.get<BusinessDashboardDto>(`/v2/analytics/business-dashboard?period=${period}`)
}

// ── DASH-006: CA Timeline ─────────────────────────────────────────────────────

export interface CaTimelineMonthDto {
  year: number
  month: number
  invoiced: number
  scheduled: number
}

export interface CaTimelineResponseDto {
  windowOffset: number
  months: CaTimelineMonthDto[]
  unplannedAmount: number
  monthlyRevenueTarget: number | null
}

export function getCaTimeline(windowOffset = 0): Promise<CaTimelineResponseDto> {
  return apiClient.get<CaTimelineResponseDto>(`/v2/analytics/ca-timeline?windowOffset=${windowOffset}`)
}

// ── DASH-007: Monthly KPI Header ─────────────────────────────────────────────

export interface MonthlyKpisDto {
  year: number
  month: number
  ventesSignees: number
  monthlySalesTarget: number | null
  /** percentage of target achieved — null when no target */
  ventesSigneesDeltaPct: number | null
  caFacture: number
  monthlyRevenueTarget: number | null
  /** percentage of target achieved — null when no target */
  caFactureDeltaPct: number | null
  // Period-over-period comparison — DASH-005
  ventesSigneesPrev: number | null
  caFacturePrev: number | null
  comparisonLabel: string | null
}

export function getMonthlyKpis(): Promise<MonthlyKpisDto> {
  return apiClient.get<MonthlyKpisDto>('/v2/analytics/monthly-kpis')
}

// ── DASH-008: Sales by Month ──────────────────────────────────────────────────

export interface SalesProjectDto {
  projectId: string
  clientName: string
  typeLabel: string
  amount: number
  planningDate: string | null  // ISO date string or null
}

export interface SalesMonthCellDto {
  amount: number
  projects: SalesProjectDto[]
}

export interface SalesPersonRowDto {
  salesPersonId: string | null
  salesPersonName: string
  isTotal: boolean
  cells: SalesMonthCellDto[]
}

export interface MonthKeyDto {
  year: number
  month: number
}

export interface SalesByMonthDto {
  months: MonthKeyDto[]
  rows: SalesPersonRowDto[]
}

export function getSalesByMonth(period: DashboardPeriod = 'month'): Promise<SalesByMonthDto> {
  return apiClient.get<SalesByMonthDto>(`/v2/analytics/sales-by-month?period=${period}`)
}

// ── SOLY-FINITION-001 MS-04: rework KPI (taux de finition) ──────────────────
// SOLY-PLAN-003: savProjectsCount / savRate are gone — the BE stopped sending them with the Sav
// nature itself, and the projects they counted now land in the finition numerator.

export interface ReworkRatesPeriodDto {
  finitionProjectsCount: number
  totalProjectsInExecution: number
  /** 0..1 ratio (0 when the denominator is 0). */
  finitionRate: number
}

export interface ReworkRatesDto {
  period: string
  current: ReworkRatesPeriodDto
  previous: ReworkRatesPeriodDto
}

export function getReworkRates(period: DashboardPeriod = 'month'): Promise<ReworkRatesDto> {
  return apiClient.get<ReworkRatesDto>(`/v2/analytics/rework-rates?period=${period}`)
}
